import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { controlRadiusClass } from '@/components/ui/field-surface'

const badgeVariants = cva(
  `inline-flex items-center justify-center ${controlRadiusClass} border px-2 py-0.5 text-[11px] font-medium leading-none w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden`,
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary-hover',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border-border/80 bg-background text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',

        /**
         * Severity pill — tinted brand surface + matching ink, driven by
         * `data-severity="critical|high|medium|low"`. Uses DB vocabulary
         * directly (Lead 2026-04-23: NO mapping to critical/warning/info).
         */
        severity: [
          'capitalize',
          'data-[severity=critical]:border-brand-critical/20 data-[severity=critical]:bg-brand-critical-bg data-[severity=critical]:text-brand-critical-text',
          'data-[severity=high]:border-brand-amber/25 data-[severity=high]:bg-brand-amber-bg data-[severity=high]:text-brand-amber-text',
          'data-[severity=medium]:border-border data-[severity=medium]:bg-secondary data-[severity=medium]:text-secondary-foreground',
          'data-[severity=low]:border-border data-[severity=low]:bg-muted data-[severity=low]:text-muted-foreground',
        ].join(' '),

        /**
         * Status pill — neutral surface + tone driven by
         * `data-status="<ProblemStatus>"`. Statuses fall into 4 visual buckets:
         *   open/in-progress: secondary
         *   resolved/verifying/wont_fix/duplicate: signal (success)
         *   regressed: destructive
         *   acknowledged/muted: outline
         */
        status: [
          'capitalize',
          // open/in-progress (default = secondary tone)
          'border-border/80 bg-secondary text-secondary-foreground',
          // success-ish
          'data-[status=resolved]:border-transparent data-[status=resolved]:bg-brand-signal-bg data-[status=resolved]:text-brand-signal-text',
          'data-[status=verifying]:border-transparent data-[status=verifying]:bg-brand-signal-bg data-[status=verifying]:text-brand-signal-text',
          'data-[status=aient-active]:border-transparent data-[status=aient-active]:bg-brand-signal-bg data-[status=aient-active]:text-brand-signal-text',
          'data-[status=wont_fix]:border-border data-[status=wont_fix]:bg-muted data-[status=wont_fix]:text-muted-foreground',
          'data-[status=duplicate]:border-border data-[status=duplicate]:bg-muted data-[status=duplicate]:text-muted-foreground',
          // failure-ish
          'data-[status=regressed]:border-brand-critical/20 data-[status=regressed]:bg-brand-critical-bg data-[status=regressed]:text-brand-critical-text',
          'data-[status=new]:border-brand-critical/20 data-[status=new]:bg-brand-critical-bg data-[status=new]:text-brand-critical-text',
          // dim states
          'data-[status=acknowledged]:border-brand-stone data-[status=acknowledged]:bg-brand-ink/[0.04] data-[status=acknowledged]:text-brand-ink',
          'data-[status=muted]:bg-transparent data-[status=muted]:text-muted-foreground',
        ].join(' '),

        /**
         * Signal-type pill — same type as other card pills; outline surface
         * for Exception, HttpError, Performance, Anomaly, etc.
         */
        'signal-type':
          'border-border/80 bg-background text-foreground',

        /**
         * "NEW" pill — small celebratory marker for arrival animation
         * (deferred to follow-up PR; variant lands now for consumer parity).
         */
        new: 'border-transparent bg-brand-signal text-brand-on-signal text-[10px] uppercase tracking-wider px-1.5',

        /**
         * Priority pill (P0..P3) — driven by `data-priority="p0|p1|p2|p3"`.
         * P0 = highest urgency, mapped to severity-critical tone; falls off
         * to muted by P3.
         */
        priority: [
          'uppercase tracking-wide font-mono text-[10px]',
          'border-border bg-muted text-muted-foreground',
          'data-[priority=p0]:border-transparent data-[priority=p0]:bg-severity-critical data-[priority=p0]:text-white',
          'data-[priority=p1]:border-transparent data-[priority=p1]:bg-severity-high data-[priority=p1]:text-white',
          'data-[priority=p2]:border-transparent data-[priority=p2]:bg-severity-medium data-[priority=p2]:text-brand-on-signal',
          'data-[priority=p3]:border-border data-[priority=p3]:bg-transparent data-[priority=p3]:text-muted-foreground',
        ].join(' '),
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span'

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
